import React, { useState } from "react"

import { useCovalOnly } from "@/feature-flags"
import { Button } from "@coval/ui"
import { cn } from "@coval/ui/cn"
import { Tooltip } from "@coval/ui/tooltip"
import { Laptop } from "lucide-react"

import {
  getCurrentDeployment,
  LOCAL_PIPELINE_TITLE,
} from "@/lib/local-worker-launch"
import { setDevId, useDevId } from "@/hooks/useDevId"

import { LocalWorkerDialog } from "./LocalWorkerDialog"

export const LocalWorkerIndicator: React.FC = () => {
  const isCovalOnly = useCovalOnly()
  const devId = useDevId()
  const [isOpen, setIsOpen] = useState(false)

  if (!isCovalOnly && !devId) {
    return null
  }

  const deployment = getCurrentDeployment()
  const tooltip = devId
    ? `Launches are going to the Local Pipeline matching COVAL_DEV_ID=${devId}, against ${deployment}. Click to change or clear.`
    : LOCAL_PIPELINE_TITLE

  return (
    <>
      {/* Right: the indicator sits in the narrow sidebar rail, which has more room beside it than above it. */}
      <Tooltip side="right" content={<div className="max-w-xs">{tooltip}</div>}>
        <Button
          variant="secondary"
          onClick={() => setIsOpen(true)}
          aria-label={
            devId
              ? `Running on Local Pipeline ${devId}. Change or clear.`
              : LOCAL_PIPELINE_TITLE
          }
          data-testid="local-worker-indicator"
          className={cn(
            "w-full justify-start gap-2 px-2 font-normal [&>svg]:size-4",
            devId
              ? "border-transparent bg-coval-warning-surface text-coval-warning-on-surface ring-1 ring-coval-warning-border hover:bg-coval-warning-surface"
              : "border-coval-border bg-transparent text-coval-text-muted hover:bg-surface-0-hover",
          )}
        >
          <Laptop className="shrink-0" />
          {devId ? (
            <span className="flex min-w-0 flex-1 items-baseline gap-1">
              <span className="truncate font-mono text-xs">{devId}</span>
              <span className="shrink-0 text-[10px] opacity-80">
                {deployment}
              </span>
            </span>
          ) : (
            <span className="truncate text-xs">Local Pipeline</span>
          )}
        </Button>
      </Tooltip>

      {isOpen && (
        <LocalWorkerDialog
          onClose={() => setIsOpen(false)}
          devId={devId}
          onDevIdChange={setDevId}
        />
      )}
    </>
  )
}
